import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import {
  exportIntensityDataCSV,
  fetchIntensityReports,
  checkIntensityReportStatus,
  downloadIntensityReport,
  extendIntensityReportExpiration,
} from "../../../actions/exportCSVActions";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { format } from "date-fns";

const IntensityReportPage = () => {
  const dispatch = useDispatch();

  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [reports, setReports] = useState([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [pollingJobs, setPollingJobs] = useState(new Set());
  const pollingIntervalsRef = useRef({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadReports = async (preserveOptimisticJobs = false) => {
    setIsLoadingReports(true);
    try {
      const reportsData = await dispatch(fetchIntensityReports());
      if (Array.isArray(reportsData)) {
        setReports(prevReports => {
          if (preserveOptimisticJobs) {
            // Merge server data with any optimistic updates
            // Keep optimistic jobs that aren't in server response yet (race condition)
            const serverJobIds = new Set(reportsData.map(r => r.job_id));
            const optimisticJobs = prevReports.filter(r => !serverJobIds.has(r.job_id));
            return [...reportsData, ...optimisticJobs];
          }
          return reportsData;
        });

        // Start polling for pending/processing jobs
        reportsData.forEach(report => {
          if (
            (report.status === "pending" || report.status === "processing") &&
            !pollingIntervalsRef.current[report.job_id]
          ) {
            startPolling(report.job_id);
          }
        });
      }
    } catch (error) {
      console.error("Failed to load reports:", error);
    } finally {
      setIsLoadingReports(false);
    }
  };

  useEffect(() => {
    loadReports();

    // Cleanup polling intervals on unmount
    return () => {
      Object.values(pollingIntervalsRef.current).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, []);

  const startPolling = jobId => {
    if (pollingIntervalsRef.current[jobId]) {
      return; // Already polling
    }

    setPollingJobs(prev => new Set(prev).add(jobId));

    const interval = setInterval(async () => {
      try {
        const job = await dispatch(checkIntensityReportStatus(jobId));
        if (job) {
          setReports(prevReports => prevReports.map(r => (r.job_id === jobId ? job : r)));

          // Stop polling if job is completed or failed
          if (job.status === "completed" || job.status === "failed" || job.status === "expired") {
            stopPolling(jobId);
          }
        }
      } catch (error) {
        console.error(`Failed to check status for job ${jobId}:`, error);
        stopPolling(jobId);
      }
    }, 5000); // Poll every 5 seconds

    pollingIntervalsRef.current[jobId] = interval;
  };

  const stopPolling = jobId => {
    if (pollingIntervalsRef.current[jobId]) {
      clearInterval(pollingIntervalsRef.current[jobId]);
      delete pollingIntervalsRef.current[jobId];
    }
    setPollingJobs(prev => {
      const newSet = new Set(prev);
      newSet.delete(jobId);
      return newSet;
    });
  };

  const handleExport = async e => {
    if (e) e.preventDefault();

    try {
      const jobId = await dispatch(
        exportIntensityDataCSV({
          from_date: format(fromDate, "yyyy-MM-dd"),
          to_date: format(toDate, "yyyy-MM-dd"),
        })
      );

      if (jobId) {
        // Immediately add the new job to the list (optimistic update)
        const newJob = {
          job_id: jobId,
          from_date: format(fromDate, "yyyy-MM-dd"),
          to_date: format(toDate, "yyyy-MM-dd"),
          status: "pending",
          created_at: new Date().toISOString(),
          completed_at: null,
          file_name: null,
          error_message: null,
        };

        setReports(prevReports => [newJob, ...prevReports]);

        // Start polling immediately for the new job
        startPolling(jobId);

        // Show success toast
        setSnackbar({
          open: true,
          message: "Report generation started. Check the status below.",
          severity: "success",
        });

        // Also refresh the full list to get accurate data from server
        // Preserve optimistic jobs in case of race condition (server hasn't created job yet)
        loadReports(true);
      }
    } catch (error) {
      console.error("Failed to create report:", error);
      // Show error toast
      setSnackbar({
        open: true,
        message: "Failed to start report generation. Please try again.",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDownload = (jobId, fileName) => {
    dispatch(downloadIntensityReport(jobId, fileName));
  };

  const handleExtendExpiration = async jobId => {
    try {
      const updatedJob = await dispatch(extendIntensityReportExpiration(jobId, 30));
      if (updatedJob) {
        // Update the report in the list
        setReports(prevReports => prevReports.map(r => (r.job_id === jobId ? updatedJob : r)));
        setSnackbar({
          open: true,
          message: "Report expiration extended by 30 days.",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Failed to extend expiration:", error);
      setSnackbar({
        open: true,
        message: "Failed to extend expiration. Please try again.",
        severity: "error",
      });
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case "pending":
        return "warning";
      case "processing":
        return "info";
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "expired":
        return "default";
      default:
        return "default";
    }
  };

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  return (
    <MainContentContainer>
      <AppBar position="sticky" sx={{ width: "100%" }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Intensity Report
          </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Generate New Report
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From"
                value={fromDate}
                onChange={setFromDate}
                textField={params => <Button {...params} />}
              />
              <DatePicker label="To" value={toDate} onChange={setToDate} textField={params => <Button {...params} />} />
            </LocalizationProvider>
            <Button type="button" variant="contained" color="primary" onClick={handleExport}>
              Generate Report
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Past Reports
          </Typography>
          {isLoadingReports ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : reports.length === 0 ? (
            <Typography sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
              No reports found. Generate a new report to get started.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date Range</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map(report => {
                    const isExpiringSoon = report.is_expiring_soon || false;
                    return (
                      <TableRow
                        key={report.job_id}
                        sx={{
                          backgroundColor: isExpiringSoon ? "rgba(255, 152, 0, 0.1)" : "transparent",
                          "&:hover": {
                            backgroundColor: isExpiringSoon ? "rgba(255, 152, 0, 0.15)" : "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <TableCell>
                          {report.from_date} - {report.to_date}
                        </TableCell>
                        <TableCell>
                          <Chip label={report.status} color={getStatusColor(report.status)} size="small" />
                        </TableCell>
                        <TableCell>{formatDate(report.created_at)}</TableCell>
                        <TableCell>{report.completed_at ? formatDate(report.completed_at) : "N/A"}</TableCell>
                        <TableCell>{report.expires_at ? formatDate(report.expires_at) : "N/A"}</TableCell>
                        <TableCell>
                          {report.status === "completed" ? (
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleDownload(report.job_id, report.file_name)}
                              >
                                Download
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                onClick={() => handleExtendExpiration(report.job_id)}
                              >
                                Extend
                              </Button>
                            </Box>
                          ) : report.status === "failed" ? (
                            <Typography variant="caption" color="error">
                              {report.error_message || "Generation failed"}
                            </Typography>
                          ) : report.status === "expired" ? (
                            <Typography variant="caption" color="text.secondary">
                              Expired
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              In progress...
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainContentContainer>
  );
};

export default IntensityReportPage;
